import React, { useState, useRef, useEffect } from 'react';
import { Modal, Box, IconButton, Typography, Fade, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FiX, FiZoomIn, FiZoomOut, FiRotateCw, FiDownload, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { downloadFile } from 'Helpers';

const ImageViewerModal = ({ open, onClose, images = [], initialIndex = 0, title = '' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });

    const containerRef = useRef(null);
    const imageRef = useRef(null);

    // All images
    const currentItem = images[currentIndex];

    // Reset transformations when image changes
    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setRotation(0);
    }, [currentIndex]);

    // Ensure default zoom when modal opens
    useEffect(() => {
        if (open) {
            setScale(1);
            setPosition({ x: 0, y: 0 });
            setRotation(0);
        }
    }, [open]);

    // Handle keyboard events
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!open) return;

            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
                case '+':
                case '=':
                    handleZoomIn();
                    break;
                case '-':
                    handleZoomOut();
                    break;
                case '0':
                    handleReset();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [open, currentIndex]);

    const handleZoomIn = () => {
        setScale((prev) => Math.min(prev * 1.2, 5));
    };

    const handleZoomOut = () => {
        setScale((prev) => Math.max(prev / 1.2, 0.1));
    };

    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        setRotation(0);
    };

    const handleRotate = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    const handleNext = () => {
        if (currentIndex < images.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    const handleDownload = () => {
        if (currentItem) {
            downloadFile(currentItem);
        }
    };

    // Mouse wheel zoom
    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY;
        if (delta < 0) {
            handleZoomIn();
        } else {
            handleZoomOut();
        }
    };

    // Touch and mouse drag handlers
    const handleMouseDown = (e) => {
        if (scale <= 1) return;

        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
        setLastPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
        if (!isDragging || scale <= 1) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        if (scale <= 1 || e.touches.length !== 1) return;

        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y
        });
    };

    const handleTouchMove = (e) => {
        if (!isDragging || scale <= 1 || e.touches.length !== 1) return;

        e.preventDefault();
        const touch = e.touches[0];
        const newX = touch.clientX - dragStart.x;
        const newY = touch.clientY - dragStart.y;

        setPosition({ x: newX, y: newY });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    if (!open || images.length === 0) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: '90vw', md: '60vw' },
                        height: { xs: '80vh', md: '70vh' },
                        bgcolor: 'black',
                        borderRadius: 2,
                        overflow: 'hidden',
                        outline: 'none',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, transparent 100%)',
                            zIndex: 10,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                background: 'rgba(255,255,255,0.1)',
                                padding: '0.5rem 1rem',
                                borderRadius: '50px',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            {title && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', md: '1.25rem' }
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: { xs: '0.875rem', md: '1rem' }
                                }}
                            >
                                {currentIndex + 1} / {images.length}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.2)'
                                }
                            }}
                        >
                            <FiX size={20} />
                        </IconButton>
                    </Box>

                    {/* Controls */}
                    {true && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'rgba(0,0,0,0.8)',
                                borderRadius: '25px',
                                p: 1,
                                display: 'flex',
                                gap: 1,
                                zIndex: 10,
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={scale <= 0.1}
                                sx={{
                                    color: 'white',
                                    '&:disabled': { color: 'rgba(255,255,255,0.3)' }
                                }}
                                size="small"
                            >
                                <FiZoomOut size={18} />
                            </IconButton>

                            <Typography
                                sx={{
                                    color: 'white',
                                    minWidth: '60px',
                                    textAlign: 'center',
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {Math.round(scale * 100)}%
                            </Typography>

                            <IconButton
                                onClick={handleZoomIn}
                                disabled={scale >= 5}
                                sx={{
                                    color: 'white',
                                    '&:disabled': { color: 'rgba(255,255,255,0.3)' }
                                }}
                                size="small"
                            >
                                <FiZoomIn size={18} />
                            </IconButton>

                            <Box sx={{ width: '1px', bgcolor: 'rgba(255,255,255,0.3)', mx: 0.5 }} />

                            <IconButton onClick={handleRotate} sx={{ color: 'white' }} size="small">
                                <FiRotateCw size={18} />
                            </IconButton>

                            <IconButton onClick={handleReset} sx={{ color: 'white' }} size="small">
                                Reset
                            </IconButton>

                            <IconButton onClick={handleDownload} sx={{ color: 'white' }} size="small">
                                <FiDownload size={18} />
                            </IconButton>
                        </Box>
                    )}

                    {/* Navigation Arrows */}
                    {images.length > 1 && (
                        <>
                            <IconButton
                                onClick={handlePrevious}
                                disabled={currentIndex === 0}
                                sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    zIndex: 10,
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.7)'
                                    },
                                    '&:disabled': {
                                        color: 'rgba(255,255,255,0.3)'
                                    }
                                }}
                            >
                                <FiChevronLeft size={24} />
                            </IconButton>

                            <IconButton
                                onClick={handleNext}
                                disabled={currentIndex === images.length - 1}
                                sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    zIndex: 10,
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.7)'
                                    },
                                    '&:disabled': {
                                        color: 'rgba(255,255,255,0.3)'
                                    }
                                }}
                            >
                                <FiChevronRight size={24} />
                            </IconButton>
                        </>
                    )}

                    {/* Media Container */}
                    <Box
                        ref={containerRef}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <img
                            ref={imageRef}
                            src={currentItem}
                            alt={`Media item ${currentIndex + 1}`}
                            style={{
                                maxWidth: scale <= 1 ? '100%' : 'none',
                                maxHeight: scale <= 1 ? '100%' : 'none',
                                width: scale > 1 ? 'auto' : undefined,
                                height: scale > 1 ? 'auto' : undefined,
                                objectFit: 'contain',
                                transform: `scale(${scale}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
                                transformOrigin: 'center center',
                                transition: isDragging ? 'none' : 'transform 0.3s ease',
                                userSelect: 'none',
                                pointerEvents: 'none'
                            }}
                            draggable={false}
                            onLoad={() => {
                                // Reset position when image loads
                                setPosition({ x: 0, y: 0 });
                            }}
                        />
                    </Box>

                    {/* Mobile Touch Instructions */}
                    {isMobile && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 80,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                background: 'rgba(0,0,0,0.6)',
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                zIndex: 10
                            }}
                        >
                            Pinch to zoom • Drag to pan • Tap controls to adjust
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ImageViewerModal;
